import { ChangeEvent, ReactNode, SyntheticEvent, useMemo, useState } from "react";
import { Dayjs } from "dayjs";
import "dayjs/locale/th";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { createExpressDocument, updateExpressDocument } from "../../services/expressDocument";
import { ExpressContact } from "../../types/express-master.type";
import { ExpressCreditBill, ExpressDocumentDetail } from "../../types/express-document.type";
import { featureAccess } from "../../permissions/access";
import { useUserRole } from "../../utils/hooks/useUserRole";
import { formatNumber } from "../../utils";
import { useBranches } from "../SpecialOrder/useBranches";
import { CustomerOption } from "../SpecialOrder/CustomerPicker";
import BillLinesGrid from "./BillLinesGrid";
import DocumentPanel from "./DocumentPanel";
import LinesGrid from "./LinesGrid";
import PartyPanel from "./PartyPanel";
import PaymentBlock from "./PaymentBlock";
import TaxTab from "./TaxTab";
import TotalsBlock, { TotalsRow } from "./TotalsBlock";
import {
  BILL_LINES_TAB_LABEL,
  ExpressDocTypeConfig,
  LINES_TAB_LABEL,
  MONEY_DECIMALS,
  NO_VAT_PRICE_TYPE,
  TAX_REPORT_TAB_LABEL,
} from "./constants";
import {
  BillLineDraft,
  DocumentDraft,
  LineDraft,
  createInitialDraft,
  documentVatRate,
  draftBillPayTotal,
  draftFromDocument,
  draftPaymentTotals,
  draftTotals,
  partyFromCustomer,
  sanitizeDecimal,
  storedPaymentTotals,
  storedTotals,
} from "./documentDraft";
import {
  buildDocumentRequest,
  validateDocumentDraft,
  withContact,
  withCreditBills,
  withSupplierBillDate,
} from "./documentRules";
import { taxReportRow } from "./taxReport";

type FormTab = "LINES" | "TAX";

interface DocumentFormProps {
  config: ExpressDocTypeConfig;
  /** The saved document; null while a new one is keyed. */
  document: ExpressDocumentDetail | null;
  readOnly: boolean;
  /** What a viewed document offers instead of the save buttons (edit, cancel, back). */
  viewActions: ReactNode;
  /** The document the server stored; null when it answered without one. */
  onSaved: (saved: ExpressDocumentDetail | null) => void;
  onDiscard: () => void;
}

const TOTALS_WIDTH = { xs: "100%", sm: 440 };

/**
 * The one entry form of the four Express types, laid out like the Express screen: who the
 * document is with on the left, its number and date on the right, the lines in tabs, the totals
 * at the bottom right and, for O1 / E1, how it was paid. What a type adds or leaves out comes
 * from its config. The totals are a preview of the server's rule while the document is keyed;
 * a viewed document shows the totals the server stored.
 */
export default function DocumentForm({
  config,
  document,
  readOnly,
  viewActions,
  onSaved,
  onDiscard,
}: Readonly<DocumentFormProps>) {
  const role = useUserRole();
  const canSelectBranch = featureAccess.expressDocumentAllBranches(role);
  const isNew = document === null;
  const requiresBranch = canSelectBranch && isNew;
  const branches = useBranches(requiresBranch);
  const isReceipt = config.lineMode === "BILLS";

  const [draft, setDraft] = useState<DocumentDraft>(() =>
    document ? draftFromDocument(document) : createInitialDraft(config),
  );
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [tab, setTab] = useState<FormTab>("LINES");

  const vatRate = documentVatRate(document);
  const selectedBranch = branches.find((branch) => branch.uuid === draft.branchUuid);
  const branchCode = isNew ? selectedBranch?.branch_code : document.branch_code;
  const errors = useMemo(
    () => (isSubmitted ? validateDocumentDraft(draft, config, { requiresBranch, vatRate }) : {}),
    [isSubmitted, draft, config, requiresBranch, vatRate],
  );
  const hasErrors = Object.keys(errors).length > 0;
  const isStored = readOnly && document !== null;
  const totals = isStored ? storedTotals(document) : draftTotals(draft, config, vatRate);
  const paymentTotals = isStored ? storedPaymentTotals(document) : draftPaymentTotals(draft);
  const taxRow = config.taxReport
    ? taxReportRow({
        kind: config.taxReport,
        draft,
        totals,
        docNo: document?.doc_no ?? "",
        departmentCode: document?.department_code ?? "",
      })
    : null;
  const creditBillParams =
    isReceipt && draft.party
      ? {
          customer_code: draft.party.code,
          branch_code: canSelectBranch ? branchCode : undefined,
          exclude_document_uuid: document?.uuid,
        }
      : null;

  const handleChange = (patch: Partial<DocumentDraft>) =>
    setDraft((previous) => ({ ...previous, ...patch }));
  // A receipt's customer and bills belong to the branch, so a new branch starts them over.
  const handleBranchChange = (branchUuid: string) =>
    setDraft((previous) =>
      isReceipt
        ? { ...previous, branchUuid, customer: null, party: null, billLines: [], salespersonCode: "" }
        : { ...previous, branchUuid },
    );
  const handleContactChange = (contact: ExpressContact | null) =>
    setDraft((previous) => withContact(previous, contact, config));
  const handleCustomerChange = (customer: CustomerOption | null) =>
    setDraft((previous) => ({
      ...previous,
      customer,
      party: customer ? partyFromCustomer(customer) : null,
      billLines: [],
      salespersonCode: "",
    }));
  const handleSupplierBillDateChange = (date: Dayjs | null) =>
    setDraft((previous) => withSupplierBillDate(previous, date));
  const handleLinesChange = (lines: LineDraft[]) => handleChange({ lines });
  const handleBillLinesChange = (billLines: BillLineDraft[]) => handleChange({ billLines });
  const handlePickBills = (bills: ExpressCreditBill[]) =>
    setDraft((previous) => withCreditBills(previous, bills));
  const handleDiscountChange = (event: ChangeEvent<HTMLInputElement>) =>
    handleChange({ discountAmount: sanitizeDecimal(event.target.value, MONEY_DECIMALS) });
  const handleTabChange = (_event: SyntheticEvent, value: FormTab) => setTab(value);

  const handleSave = async () => {
    setIsSubmitted(true);
    const saveErrors = validateDocumentDraft(draft, config, { requiresBranch, vatRate });
    if (Object.keys(saveErrors).length > 0) {
      if (saveErrors.lines || saveErrors.billLines) setTab("LINES");
      return;
    }
    setSaving(true);
    try {
      const body = buildDocumentRequest(draft, config, canSelectBranch ? branchCode : undefined);
      const response = isNew
        ? await createExpressDocument(body)
        : await updateExpressDocument(document.uuid, body);
      onSaved(response.data?.data ?? null);
    } catch {
      // The interceptor already shows the server's reason (an unbalanced receipt, an overpaid bill).
    } finally {
      setSaving(false);
    }
  };

  const linesTab = isReceipt ? (
    <BillLinesGrid
      billLines={draft.billLines}
      readOnly={readOnly}
      creditBillParams={creditBillParams}
      error={errors.billLines}
      onChange={handleBillLinesChange}
      onPick={handlePickBills}
    />
  ) : (
    <LinesGrid
      config={config}
      lines={draft.lines}
      readOnly={readOnly}
      departmentCode={document?.department_code ?? ""}
      error={errors.lines}
      onChange={handleLinesChange}
    />
  );

  const discountInput = config.hasDiscount
    ? {
        text: draft.discountAmount,
        isEditable: !readOnly,
        error: errors.discountAmount,
        onChange: handleDiscountChange,
      }
    : null;

  const totalsBlock = isReceipt ? (
    <TotalsRow label="ยอดชำระตามใบกำกับ" isGrandTotal>
      {formatNumber(draftBillPayTotal(draft))}
    </TotalsRow>
  ) : (
    <TotalsBlock
      totals={totals}
      vatRate={vatRate}
      hasVat={draft.priceType !== NO_VAT_PRICE_TYPE}
      discount={discountInput}
    />
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
      <Stack gap={2}>
        {hasErrors && <Alert severity="error">กรุณาตรวจสอบข้อมูลที่ยังไม่ครบหรือไม่ถูกต้อง</Alert>}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 7 }}>
            <Paper sx={{ height: "100%" }}>
              <PartyPanel
                config={config}
                draft={draft}
                errors={errors}
                readOnly={readOnly}
                isCustomerLocked={!isNew}
                customerBranchUuid={selectedBranch?.uuid}
                isCustomerSearchBlocked={requiresBranch && !selectedBranch}
                onChange={handleChange}
                onContactChange={handleContactChange}
                onCustomerChange={handleCustomerChange}
                onSupplierBillDateChange={handleSupplierBillDateChange}
              />
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <Paper sx={{ height: "100%" }}>
              <DocumentPanel
                config={config}
                draft={draft}
                errors={errors}
                readOnly={readOnly}
                document={document}
                branches={requiresBranch ? branches : null}
                onChange={handleChange}
                onBranchChange={handleBranchChange}
              />
            </Paper>
          </Grid>
        </Grid>
        <Paper>
          <Tabs value={tab} onChange={handleTabChange} sx={{ mb: 2 }}>
            <Tab value="LINES" label={isReceipt ? BILL_LINES_TAB_LABEL : LINES_TAB_LABEL} />
            {config.taxReport && <Tab value="TAX" label={TAX_REPORT_TAB_LABEL[config.taxReport]} />}
          </Tabs>
          {tab === "LINES" && linesTab}
          {tab === "TAX" && config.taxReport && <TaxTab kind={config.taxReport} row={taxRow} />}
        </Paper>
        <Stack direction="row" justifyContent="flex-end">
          <Paper sx={{ width: TOTALS_WIDTH }}>
            {totalsBlock}
          </Paper>
        </Stack>
        {config.hasPayment && (
          <Paper>
            <PaymentBlock
              cashAmount={draft.cashAmount}
              whtAmount={draft.whtAmount}
              paymentLines={draft.paymentLines}
              paymentTotals={paymentTotals}
              readOnly={readOnly}
              errors={errors}
              onChange={handleChange}
            />
          </Paper>
        )}
        <Stack direction="row" justifyContent="flex-end" gap={1}>
          {readOnly ? (
            viewActions
          ) : (
            <>
              <Button variant="text" disabled={saving} onClick={onDiscard}>
                ยกเลิก
              </Button>
              <Button variant="contained" loading={saving} onClick={handleSave}>
                บันทึก
              </Button>
            </>
          )}
        </Stack>
      </Stack>
    </LocalizationProvider>
  );
}
